import { Text } from "@mantine/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useCounter, useDebouncedValue } from "@mantine/hooks";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import styled, { css } from "styled-components";
import {
  getDailyActivityReport,
  getDailyActivityReportExcel,
} from "../../api/reportsApi";
import { useAuth } from "../../context/AuthContext";
import { errorNotification } from "../../utils/useNotification";
import { downloadBlobFile } from "../../utils/downloadFile";
import CustomSelect from "../common/Inputs/CustomSelect";
import DataTable, { Column } from "../common/Table/DataTable";
import ReportFilterBar from "./ReportFilterBar";
import { EmployeeCell, LabelCell, TerminalChip } from "./ReportCells";
import { ResultCount, sortHeader } from "./reportTableUtils";
import MissingExitCorrection, {
  CorrectionTarget,
} from "./MissingExitCorrection";

/** Sunucunun beklediği tarih biçimi (uçlar `new Date(...)` ile ayrıştırıyor). */
const API_DATE_FORMAT = "YYYY-MM-DDTHH:mm:ss[Z]";

/**
 * Arka uçtan gelen hareket durumları.
 *
 * `auto` ("Otomatik") üçüncü bir durumdur, "vaktinde"nin çeşidi değil: personel
 * hiç okutmamış, kaydı `autoCheckout` job'ı mesai saatine yazmıştır. Ayrı
 * gösterilmesinin sebebi bu kayıtların tam mesai bitişine yazılması, yani
 * ölçüte göre daima "vaktinde" çıkmasıydı.
 */
const STATUS_LABELS: Record<string, string> = {
  onTime: "Vaktinde",
  late: "Geç",
  early: "Erken",
  auto: "Otomatik",
};

// Üç renk: geç/erken kırmızı, otomatik nötr gri (ne övgü ne suçlama —
// personel okutmamış, sistem yazmış), vaktinde yeşil. Hap biçimi panelin
// diğer durum rozetleriyle (Çalışanlar tablosu) aynı.
/**
 * Durum süzgecinin seçenekleri — etiketler `STATUS_LABELS`'tan türetiliyor ki
 * süzgeç ile hücre rozeti asla ayrışmasın.
 *
 * Eşleşme kuralı (backend): satırın giriş VEYA çıkış durumu seçilen değere
 * eşitse geçer. `late` yalnızca girişte, `early` yalnızca çıkışta üretildiği
 * için bu ikisi tam isabetli; etiketler de o yüzden "Geç giriş"/"Erken çıkış".
 */
const STATUS_FILTER_OPTIONS = [
  { value: "late", label: "Geç giriş" },
  { value: "early", label: "Erken çıkış" },
  { value: "onTime", label: STATUS_LABELS.onTime },
  { value: "auto", label: STATUS_LABELS.auto ?? "Otomatik" },
];

const STATUS_TONES: Record<string, { fg: string; bg: string; border: string }> = {
  late: { fg: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  early: { fg: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  auto: { fg: "#6b7280", bg: "#f3f4f6", border: "#e5e7eb" },
  onTime: { fg: "#059669", bg: "#ecfdf5", border: "#a7f3d0" },
};

/**
 * Satırın iş gününü gösterir. `workday` sunucudan `2026-08-17` biçiminde gelir
 * ve giriş/çıkıştan bağımsızdır — girişi okutulmamış günde de doğrudur.
 * Yerel bileşenlerle kurulur, aksi halde `new Date("2026-08-17")` UTC gece
 * yarısı sayılıp negatif ofsetli makinelerde bir gün geri kayardı.
 */
const formatWorkday = (workday?: string, entry?: string | null): string => {
  if (workday) {
    const [year, month, day] = workday.split("-").map(Number);
    if (year && month && day) {
      return new Date(year, month - 1, day).toLocaleDateString("tr-TR");
    }
  }
  return entry ? new Date(entry).toLocaleDateString("tr-TR") : "-";
};

const formatTime = (value?: string | null): string | null =>
  value
    ? new Date(value).toLocaleTimeString("tr-TR", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

/** Saat + durum hapı. Okutma yoksa boş hücre değil, soluk bir tire gösterilir. */
const ActivityCell = ({
  value,
  status,
}: {
  value?: string | null;
  status?: string;
}) => {
  const time = formatTime(value);

  if (!time && !status) {
    return (
      <Text fw={400} size="md" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <ActivityWrap>
      <Text fw={500} size="md" style={{ color: "#111827" }}>
        {time ?? "—"}
      </Text>
      {status && <StatusPill $status={status}>{STATUS_LABELS[status] ?? status}</StatusPill>}
    </ActivityWrap>
  );
};

/**
 * ÇIKIŞ hücresi — düzeltme katmanını da gösterir (plan 2.49-B).
 *
 * Üç hâl var ve üçü de ayırt edilebilir olmalı:
 *
 *   1. Çıkış okutulmuş  → saat + durum hapı (`ActivityCell` ile aynı)
 *   2. Eksik çıkış      → "Düzelt" düğmesi; gün kapandı, İK'nın işi var
 *   3. Düzeltilmiş      → beyan edilen saat + "düzeltildi" rozeti + not
 *
 * ⚠ 3. hâlde saat NORMAL bir çıkış gibi gösterilmiyor. Gösterilseydi ölçüm ile
 * beyan tek görünüme düşerdi ve "bu saat okutuldu mu, yazıldı mı?" sorusu bir
 * daha cevaplanamazdı — `autoGenerated`'ın başa bela olmasının sebebi tam
 * buydu (plan 2.25, 2.36).
 */
const ExitCell = ({
  row,
  onCorrect,
}: {
  row: any;
  onCorrect: (row: any) => void;
}) => {
  if (row.corrected) {
    return (
      <ActivityWrap>
        <Text fw={500} size="md" style={{ color: "#111827" }}>
          {formatTime(row.correctedExit) ?? "—"}
        </Text>
        <CorrectedPill
          onClick={() => onCorrect(row)}
          title={row.correctionNote ?? ""}
        >
          düzeltildi
        </CorrectedPill>
      </ActivityWrap>
    );
  }

  if (row.missingExit) {
    return (
      <ActivityWrap>
        <Text fw={400} size="md" c="dimmed">
          —
        </Text>
        <CorrectButton onClick={() => onCorrect(row)}>Düzelt</CorrectButton>
      </ActivityWrap>
    );
  }

  return <ActivityCell value={row.exit} status={row.exitStatus} />;
};

type Sort = { key: string; dir: "asc" | "desc" } | null;

const SORT_PARAMS: Record<string, string> = {
  name: "nameSort",
  date: "dateSort",
};

/** "Güncelle"ye basılana kadar isteğe gitmeyen alanlar. */
interface AppliedRange {
  start: string | null;
  end: string | null;
  entryBuffer: number;
  exitBuffer: number;
}

const ReportTable = () => {
  const navigate = useNavigate();
  const { departmentSelectData, tagSelectData, terminalSelectData } = useAuth();

  const tagOptions = useMemo(
    () => [...(tagSelectData?.role ?? []), ...(tagSelectData?.skill ?? [])],
    [tagSelectData]
  );

  // Rapor uçları etiketi yalnızca adıyla döndürüyor; rozet rengi/türü bu
  // eşlemeden çözülüyor (bkz. LabelCell).
  const tagLookup = useMemo(
    () => new Map(tagOptions.map((tag: any) => [tag.label, tag])),
    [tagOptions]
  );

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [entryBuffer, entryCounter] = useCounter(0, { min: 0 });
  const [exitBuffer, exitCounter] = useCounter(0, { min: 0 });

  const [applied, setApplied] = useState<AppliedRange>({
    start: null,
    end: null,
    entryBuffer: 0,
    exitBuffer: 0,
  });

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [sort, setSort] = useState<Sort>(null);
  const [department, setDepartment] = useState<string | undefined>();
  const [tag, setTag] = useState<string | undefined>();
  const [terminalId, setTerminalId] = useState<string | undefined>();
  /**
   * Durum süzgeci — "yalnızca geç kalanlar" sorusunun cevabı.
   *
   * Ayrı bir "Geç Kalanlar" ekranı yerine buraya bir süzgeç eklendi: geç/erken
   * ölçütü zaten bu raporun satırlarında hesaplanıyordu ve renkli rozetle
   * gösteriliyordu, eksik olan tek şey süzmekti. İkinci bir ekran aynı ölçüt
   * zincirini ikinci kez uygulamak zorunda kalırdı.
   */
  const [status, setStatus] = useState<string | undefined>();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  /** Düzeltme kutusunun hedefi; null ise kutu kapalı. */
  const [correcting, setCorrecting] = useState<CorrectionTarget | null>(null);
  // "Güncelle" butonunun kendi göstergesi: sayfa değiştirirken de dönmesin.
  const [applying, setApplying] = useState(false);
  const [exporting, setExporting] = useState(false);

  const requestParams = useCallback(
    () => ({
      startDate: applied.start
        ? dayjs(applied.start).format(API_DATE_FORMAT)
        : undefined,
      endDate: applied.end
        ? dayjs(applied.end).format(API_DATE_FORMAT)
        : undefined,
      entryBuffer: applied.entryBuffer,
      exitBuffer: applied.exitBuffer,
      name: debouncedSearch || undefined,
      department,
      tag,
      terminalId,
      status,
      ...(sort ? { [SORT_PARAMS[sort.key]]: sort.dir } : {}),
    }),
    [applied, debouncedSearch, department, tag, terminalId, status, sort]
  );

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const { data }: any = await getDailyActivityReport({
        ...requestParams(),
        page,
        limit,
      });
      setRows(data?.data ?? []);
      setTotalPages(data?.meta?.totalPages ?? 1);
      setTotalItems(data?.meta?.total ?? 0);
    } catch (error) {
      // Eskiden hata sessizce yutuluyordu: kullanıcı boş tabloya bakıp
      // "kayıt yok" sanıyordu.
      console.error("Giriş/çıkış raporu alınamadı:", error);
      setRows([]);
      setTotalPages(1);
      setTotalItems(0);
      errorNotification({
        title: "Rapor Yüklenemedi.",
        message: "Giriş/çıkış raporu alınırken bir hata oluştu.",
      });
    } finally {
      setLoading(false);
      setApplying(false);
    }
  }, [requestParams, page, limit]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  useEffect(() => {
    navigate(`?page=${page}&limit=${limit}`, { replace: true });
  }, [page, limit, navigate]);

  const resetToFirstPage = () => setPage(1);

  const applyRange = () => {
    if (startDate && endDate && dayjs(startDate).isAfter(dayjs(endDate))) {
      errorNotification({
        title: "Geçersiz Tarih Aralığı.",
        message: "Başlangıç tarihi, bitiş tarihinden sonra olamaz.",
      });
      return;
    }
    setApplying(true);
    resetToFirstPage();
    setApplied({ start: startDate, end: endDate, entryBuffer, exitBuffer });
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      // Arama kutusu da dosyaya yansısın: eskiden `name` gönderilmediği için
      // ekranda tek personel görünürken indirilen dosya herkesi içeriyordu.
      const { data }: any = await getDailyActivityReportExcel(requestParams());
      downloadBlobFile(data, "giris-cikis-raporu");
    } catch (error) {
      console.error("Excel dosyası indirilemedi:", error);
      errorNotification({
        title: "İndirme Başarısız.",
        message: "Excel dosyası oluşturulurken bir hata oluştu.",
      });
    } finally {
      setExporting(false);
    }
  };

  const toggleSort = (key: string) => {
    resetToFirstPage();
    setSort((prev) =>
      prev?.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  };

  const columns: Column[] = [
    {
      key: "userName",
      header: sortHeader("Ad Soyad", sort, "name"),
      width: "20%",
      onHeaderClick: () => toggleSort("name"),
      render: (row) => <EmployeeCell name={row.userName} />,
    },
    {
      key: "department",
      header: "Departman",
      width: "12%",
      render: (row) => <LabelCell value={row.department} kind="department" />,
    },
    {
      key: "tags",
      header: "Roller",
      width: "12%",
      render: (row) => (
        <LabelCell value={row.tags} refs={row.tagRefs} kind="role" lookup={tagLookup} />
      ),
    },
    {
      key: "workday",
      header: sortHeader("Tarih", sort, "date"),
      width: "14%",
      onHeaderClick: () => toggleSort("date"),
      // Tarih girişten türetilmez: girişi okutulmamış günde `entry` null olur
      // ve epoch'a (1/1/1970) düşerdi. Sunucu iş gününü `workday` yollar.
      render: (row) => (
        <Text fw={400} size="md" style={{ color: "#111827" }}>
          {formatWorkday(row.workday, row.entry)}
        </Text>
      ),
    },
    {
      key: "entry",
      header: "Giriş",
      width: "15%",
      render: (row) => <ActivityCell value={row.entry} status={row.entryStatus} />,
    },
    {
      key: "exit",
      header: "Çıkış",
      width: "15%",
      render: (row) => (
        <ExitCell
          row={row}
          onCorrect={(target) =>
            setCorrecting({
              userId: target.userId,
              userName: target.userName,
              workday: target.workday,
              corrected: Boolean(target.corrected),
              correctedExit: target.correctedExit,
              correctionNote: target.correctionNote,
            })
          }
        />
      ),
    },
    {
      key: "terminal",
      header: "Terminal",
      width: "12%",
      render: (row) => <TerminalChip code={row.terminal} />,
    },
  ];

  return (
    <>
      {/*
        Düzeltme kaydedilince tablo yeniden çekiliyor: `missingExit`,
        `workedMinutes` ve aylık sayaçlar hep sunucuda hesaplanıyor, istemci
        satırı kendi eliyle güncellerse panelin gösterdiği sayı raporun
        ürettiğinden ayrışır.
      */}
      <MissingExitCorrection
        target={correcting}
        onClose={() => setCorrecting(null)}
        onSaved={fetchActivities}
      />

      <ReportFilterBar
        mode="datetime"
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        entryBuffer={entryBuffer}
        exitBuffer={exitBuffer}
        entryCounter={entryCounter}
        exitCounter={exitCounter}
        onApply={applyRange}
        applying={applying}
        onExport={exportExcel}
        exporting={exporting}
      />

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        rowKey={(row: any) => `${row.userId ?? row.userName}-${row.workday}`}
        emptyText="Seçilen aralıkta hareket bulunamadı."
        searchValue={search}
        searchPlaceholder="Personel ara..."
        onSearchChange={(value) => {
          resetToFirstPage();
          setSearch(value);
        }}
        toolbar={
          <>
            <CustomSelect
              placeholder="Departman"
              data={departmentSelectData}
              clearable
              onChange={(value: any) => {
                resetToFirstPage();
                setDepartment(value ?? undefined);
              }}
            />
            <CustomSelect
              placeholder="Rol / Yetenek"
              data={tagOptions}
              clearable
              onChange={(value: any) => {
                resetToFirstPage();
                setTag(value ?? undefined);
              }}
            />
            <CustomSelect
              placeholder="Terminal"
              data={terminalSelectData}
              clearable
              onChange={(value: any) => {
                resetToFirstPage();
                setTerminalId(value ?? undefined);
              }}
            />
            {/*
              Seçenek adları tabloda kullanılan STATUS_LABELS ile aynı kaynaktan:
              süzgeçte "Geç" yazıp hücrede başka bir şey göstermek olmaz.
              "Otomatik" de seçenek — o kayıtlar ölçüte hiç girmiyor ve İK'nın
              "kim hiç okutmamış" sorusunun cevabı orada.
            */}
            <CustomSelect
              placeholder="Durum"
              data={STATUS_FILTER_OPTIONS}
              clearable
              onChange={(value: any) => {
                resetToFirstPage();
                setStatus(value ?? undefined);
              }}
            />
          </>
        }
        subToolbar={<ResultCount total={totalItems} />}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        limit={limit}
        onLimitChange={(value) => {
          setPage(1);
          setLimit(value);
        }}
      />
    </>
  );
};

export default ReportTable;

// Saat ve durum hapı yan yana: alt alta dizildiğinde satır yüksekliği ~90px'e
// çıkıyor ve 10 satırlık sayfa gereksiz uzuyordu. Dar ekranda alta kayar.
const ActivityWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

/** Düzeltme rozeti — durum haplarından AYRI renk: bu bir ölçüm değil, beyan. */
const CorrectedPill = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
`;

const CorrectButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  color: #1d4ed8;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
`;

const StatusPill = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;

  ${({ $status }) => {
    const tone = STATUS_TONES[$status] ?? STATUS_TONES.onTime;
    return css`
      color: ${tone.fg};
      background: ${tone.bg};
      border: 1px solid ${tone.border};
    `;
  }}
`;
